// @flow
import React, { useState, useCallback, Fragment, useEffect } from 'react';
import * as css from './AddSubscription.module.css';
import UncompletedOrder from 'views/UncompletedOrder';
import Complete from 'views/Complete';
import Confirm from 'views/Confirm';
import Header from 'views/Header';
import Hero from 'views/Hero';
import Error from 'views/Error';
import OrderSummary from 'components/OrderSummary';
import InfoAccordion from 'components/Accordion';
import PaymentAndTerms from 'components/PaymentAndTermsComviq';
import CardContainer from 'components/CardContainer';
import Loading from 'components';
import api from 'api';
import ReactGA from 'react-ga';
import { useSelector } from 'react-redux';
import { infoLogger, errorLogger } from '../../helpers';
import { getContentFor, getTermsFor } from '../../helpers/contentHelpers';
import { isComviqCustomer } from '../../helpers/customerTypeHelpers';
import { CustomerTypes } from '../../Constants';

type Props = {
  eid: string,
  msisdn: string,
  ssn: string,
  pricePlanName: string,
  primaryImsi: string,
  name: string,
  hasUncompletedOrder: boolean,
  orderId: string,
  displayPromotion: boolean,
};

const AddSubscriptionComviq = ({
  eid,
  msisdn,
  ssn,
  pricePlanName,
  primaryImsi,
  name,
  hasUncompletedOrder,
  orderId,
  displayPromotion,
}: Props) => {
  const [isComplete, setComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [newOrderId, setNewOrderId] = useState('');

  const [isConfirmed, setConfirmed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const loading = useSelector((state) => state.app.isLoading);
  const error = useSelector((state) => state.app.error);

  const customerType = CustomerTypes.FUTUR_COMVIQ;

  const allContent = getContentFor(customerType);
  const pageContent = hasUncompletedOrder ? allContent.page.readd : allContent.page.add;
  const terms = getTermsFor(customerType);

  const postData = async () => {
    const body = {
      Ssn: ssn,
      PrimaryMsisdn: msisdn,
      PrimaryImsi: primaryImsi,
      PrimaryPriceplanProductName: pricePlanName,
      SecondaryEid: eid,
      Vendor: window.__VENDOR__,
    };
    setIsLoading(true);
    try {
      // Post Order'
      const result = await api.rest('v1/orders', body, 'POST');
      if (result.error) {
        errorLogger({ name: 'client-post-order', log: 'Error in post order' });
        setErrorMsg(result.error);
        setIsError(true);
      } else {
        const customerBody = {
          Eid: eid,
          Msisdn: msisdn,
          HasUncompletedOrder: true,
          OrderId: result.orderId,
          CustomerType: customerType,
          Vendor: window.__VENDOR__,
        };
        await api.rest('v1/customerStates', customerBody, 'POST');
        setNewOrderId(result.orderId);
        setComplete(true);
      }
    } catch (error) {
      errorLogger({ name: 'client-post-order', log: `Post order or customerStates failed: ${error.message}` });
      setErrorMsg(error.message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const addSubscription = useCallback(() => {
    infoLogger({ name: 'client-add-subscription', log: 'Add subscription' });

    postData();
  }, [ssn, msisdn, pricePlanName, eid]);

  const completeSubscription = useCallback(() => {
    if (orderId) {
      infoLogger({ name: 'client-complete-subscription', log: 'Complete subscription' });

      setNewOrderId(orderId);
    }
    setComplete(true);
  }, []);

  useEffect(() => {
    if (error) {
      errorLogger({ name: 'client-add-subscription-error', log: `Error in AddSubscription: ${error.message}` });

      setErrorMsg(error.message);
      setIsError(true);
    }
    if (hasUncompletedOrder) {
      infoLogger({ name: 'client-has-uncompleted-order', log: 'Customer has uncompleted order' });
    }
    const bodyElement = document.body;
    const hasVerticalScrollbar = bodyElement.scrollHeight > bodyElement.clientHeight;
    hasVerticalScrollbar ? setIsScrolled(true) : setIsScrolled(false);
    if (isConfirmed) {
      ReactGA.pageview('/subscription');
    }
  }, [isConfirmed, error]);

  useEffect(() => {
    window.__PAGE__ = 'Add subscription';
    infoLogger({ name: 'client-page-view-add-subscription', log: 'Add subscription page shown' });
  }, []);

  if (isError) return <Error message={errorMsg} />;
  if (loading || isLoading || !css) return <Loading />;
  if (isComplete) return <Complete orderId={newOrderId} msisdn={msisdn} eId={eid} />;
  if (hasUncompletedOrder) return <UncompletedOrder onCompleteSubscription={completeSubscription} />;
  if (!isConfirmed)
    return (
      <Confirm
        onConfirm={setConfirmed}
        device={name}
        isScrolled={isScrolled}
        hasAvailablePromotion={displayPromotion}
      />
    );

  return (
    <Fragment>
      <Header />
      <Hero title={pageContent.title} />
      <CardContainer isComviq>
        <OrderSummary
          title={pageContent.subscription.title}
          amount={pageContent.subscription.name}
          price={pageContent.subscription.price}
          info={pageContent.subscription.info}
          selected={true}
          msisdn={msisdn}
          hasAvailablePromotion={displayPromotion}
        />
        <PaymentAndTerms
          hasUncompletedOrder={hasUncompletedOrder}
          content={pageContent.termsAndPayment}
          terms={terms}
          onAddSubscription={addSubscription}
        />
        <div className={css.label}>Vanliga frågor</div>
        <InfoAccordion data={pageContent.moreInfo.accordion} />
      </CardContainer>
    </Fragment>
  );
};

export default React.memo(AddSubscriptionComviq);
